import {useSlotManagementContext} from '../context/useSlotManagementContext'
import {UseAvailableTimeSlotsParams} from '../context/SlotManagementContext'
import {AvailableTimeSlotsResult} from '../requests/types'

/**
 * Strategy hook for getting available time slots.
 * Uses implementation from context (Order Change or Order Intake).
 *
 * The actual implementation is provided by the context provider and depends on:
 * - Order Change: useOrderChangeAvailableTimeSlots (handles auto-confirmable logic)
 * - Order Intake: useOrderIntakeAvailableTimeSlots (simpler API-only approach)
 *
 * @param params - Parameters for slot availability query
 * @param params.dateIndex - Date index for multi-date scenarios (default: 0)
 * @param params.slotAvailabilityQueryFn - Query function provided by app (returns query options)
 * @returns Available time slots and query state
 *
 * @example
 * ```tsx
 * const { availableSlots, queryInfo } = useAvailableTimeSlots({
 *   dateIndex: 0,
 *   slotAvailabilityQueryFn: (params) => ({
 *     queryKey: ['slots', params],
 *     queryFn: () => fetchSlots(params),
 *     enabled: true
 *   })
 * })
 * ```
 */
export const useAvailableTimeSlots = (
  params: UseAvailableTimeSlotsParams
): AvailableTimeSlotsResult => {
  const {hooks} = useSlotManagementContext()
  return hooks.useAvailableTimeSlots(params)
}
